import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Zap, Video, Users, Film, Crown, Info } from 'lucide-react';
import articleBg from '@/assets/article-bg.jpg';

interface PricingTier {
  name: string;
  credits: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Fast Draft Generation',
    credits: 'From 35 credits / sec',
    icon: <Zap className="w-6 h-6" />,
    description: 'Rapid prototyping for quick storyboards and concept visualization',
    color: 'from-blue-500 to-cyan-400',
  },
  {
    name: 'Standard Video Generation',
    credits: 'From 60 credits / sec',
    icon: <Video className="w-6 h-6" />,
    description: 'High-quality video output for professional production workflows',
    color: 'from-orange-500 to-amber-400',
  },
  {
    name: 'Character Consistency Mode',
    credits: 'From 90 credits / sec',
    icon: <Users className="w-6 h-6" />,
    description: 'Maintain character appearance across scenes with AI-driven consistency',
    color: 'from-purple-500 to-pink-400',
  },
  {
    name: 'Multi-Character Scene Mode',
    credits: 'From 110 credits / sec',
    icon: <Film className="w-6 h-6" />,
    description: 'Complex scenes with multiple characters and dynamic interactions',
    color: 'from-emerald-500 to-teal-400',
  },
  {
    name: 'Cinematic HD / Priority Render',
    credits: 'From 120–220 credits / sec',
    icon: <Crown className="w-6 h-6" />,
    description: 'Maximum quality cinematic output with priority rendering queue',
    color: 'from-red-500 to-rose-400',
  },
];

const Pricing: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="min-h-screen bg-black text-gray-300 bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url(${articleBg})` }}
    >
      <div className="min-h-screen bg-black/80">
        <div className="px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </motion.div>

          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Pricing
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl">
              Credit-based pricing that scales with your production needs. Pay only for what you use.
            </p>
          </motion.header>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium mb-1">Credit Conversion</p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  1 USD = 100 credits. Credit consumption varies by resolution, model mode, consistency features, reference materials, and rendering priority.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid gap-4">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.08 }}
                className="group relative bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/[0.07] hover:border-white/20 transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${tier.color} bg-opacity-10`}>
                      <div className="text-white">
                        {tier.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">{tier.name}</h3>
                      <p className="text-gray-400 text-sm mt-0.5">{tier.description}</p>
                    </div>
                  </div>
                  <div className="sm:text-right flex-shrink-0">
                    <span className="text-xl font-bold text-white">{tier.credits}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 text-center"
          >
            <p className="text-gray-500 text-sm">
              All pricing is subject to change. Contact us for enterprise volume discounts.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
